import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/StatusBadge";
import { Plus, Edit, Trash2, Store } from "lucide-react";
import { toast } from "sonner";

export default function EcommerceMarketplaces() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);

  const load = async () => {
    const { data } = await api.get("/ecommerce/marketplaces");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setName(""); setIsActive(true); setOpen(true); };
  const openEdit = (m) => { setEdit(m); setName(m.name); setIsActive(m.is_active); setOpen(true); };

  const save = async () => {
    if (!name.trim()) return toast.error("Pazaryeri adı gerekli");
    try {
      const payload = { name: name.trim(), is_active: isActive };
      if (edit) await api.put(`/ecommerce/marketplaces/${edit.id}`, payload);
      else await api.post("/ecommerce/marketplaces", payload);
      toast.success(edit ? "Güncellendi" : "Pazaryeri eklendi");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu pazaryeri silinsin mi?")) return;
    try {
      await api.delete(`/ecommerce/marketplaces/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#6B7280]">
          Toplam <span className="font-bold text-[#08285A]">{items.length}</span> pazaryeri
        </div>
        <Button onClick={openCreate} data-testid="mkt-new"
                className="bg-[#0B3676] hover:bg-[#08285A] text-white">
          <Plus size={16} className="mr-1" /> Yeni Pazaryeri
        </Button>
      </div>

      <div className="azd-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Pazaryeri</th>
              <th className="text-left px-4 py-3 font-semibold">Durum</th>
              <th className="text-right px-4 py-3 font-semibold">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={3} className="p-8 text-center text-[#6B7280]">
                Henüz pazaryeri tanımlanmamış. Tedarikjet, Hepsiburada, Trendyol vb. ekleyin.
              </td></tr>
            ) : items.map((m) => (
              <tr key={m.id} data-testid={`mkt-row-${m.id}`}
                  className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50">
                <td className="px-4 py-3 font-semibold text-[#111827] flex items-center gap-2">
                  <Store size={14} className="text-[#0B3676]" />
                  {m.name}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={m.is_active ? "success" : "default"}>
                    {m.is_active ? "Aktif" : "Pasif"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(m)} data-testid={`mkt-edit-${m.id}`}
                          className="p-1.5 text-[#6B7280] hover:text-[#0B3676] rounded">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => remove(m.id)} data-testid={`mkt-del-${m.id}`}
                          className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{edit ? "Pazaryeri Düzenle" : "Yeni Pazaryeri"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs font-semibold mb-1.5">Pazaryeri Adı *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Örn: Tedarikjet, Hepsiburada, Trendyol"
                     data-testid="mkt-name" autoFocus />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isActive}
                     onChange={(e) => setIsActive(e.target.checked)} />
              Aktif (satış formunda görünsün)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="mkt-save"
                    className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
